/**
 * External references (E4, spec D23 §9.2): this record's identity in another
 * system — the customer's enquiry number, a route study, the invoice. The
 * pack only SUGGESTS systems; `system` is free text. Open on a closed record
 * too: the invoice arrives after close.
 */
import { useId, useState } from 'react'
import { X } from 'lucide-react'
import { Button, Input } from '@/components/ui'
import { recordErrorMessage, useAddReference, useReferences, useRemoveReference } from '@/hooks/useRecordExtras'
import { readSummary, readUnavailable } from './readState'
import { RecordSection } from './RecordSection'
import { referencesSummary } from './sectionSummaries'
import type { ReferenceSystem } from '@/types/commerce'

export function ReferencesCard({
  orderId, suggestedSystems, focused = false,
}: {
  orderId: string
  suggestedSystems: ReferenceSystem[]
  /** `?panel=references` (spec §7.5). */
  focused?: boolean
}) {
  const references = useReferences(orderId)
  const add = useAddReference(orderId)
  const remove = useRemoveReference(orderId)
  const listId = useId()
  const [system, setSystem] = useState('')
  const [reference, setReference] = useState('')
  const [url, setUrl] = useState('')
  const [label, setLabel] = useState('')

  const systemLabel = (id: string) => suggestedSystems.find(s => s.id === id)?.label ?? id

  const handleAdd = () => {
    if (!system.trim() || !reference.trim()) return
    add.mutate({ system, reference, url, label }, {
      onSuccess: () => { setReference(''); setUrl(''); setLabel('') },
    })
  }

  return (
    <RecordSection
      id="references"
      title="External references"
      summary={readSummary(references, (d) => referencesSummary(d, suggestedSystems))}
      defaultOpen={false}
      focused={focused}
    >
      {references.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {readUnavailable(references) && <p className="text-sm text-destructive">{recordErrorMessage(references.error)}</p>}
      {references.data && references.data.length === 0 && (
        <p className="text-sm text-muted-foreground mb-3">No references to another system yet.</p>
      )}
      {(references.data?.length ?? 0) > 0 && (
        <ul className="space-y-1.5 mb-3">
          {references.data!.map(r => (
            <li key={r.id} className="flex items-center gap-2 text-sm">
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                {systemLabel(r.system)}
              </span>
              {r.url
                ? <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{r.reference}</a>
                : <span className="text-foreground">{r.reference}</span>}
              {r.label && <span className="text-xs text-muted-foreground">{r.label}</span>}
              <button type="button" aria-label={`Remove ${r.reference}`} disabled={remove.isPending}
                onClick={() => remove.mutate(r.id)}
                className="ml-auto p-0.5 rounded-full text-muted-foreground hover:bg-muted hover:text-foreground">
                <X className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="@container">
        <div className="grid gap-2 @min-[24rem]:grid-cols-2">
          <Input aria-label="System" list={listId} placeholder="System" value={system}
            onChange={e => setSystem(e.target.value)} />
          <datalist id={listId}>
            {suggestedSystems.map(s => <option key={s.id} value={s.id} label={s.label} />)}
          </datalist>
          <Input aria-label="Reference" placeholder="Reference" value={reference}
            onChange={e => setReference(e.target.value)} />
          <Input aria-label="Link (optional)" type="url" placeholder="https://…" value={url}
            onChange={e => setUrl(e.target.value)} />
          <Input aria-label="Label (optional)" placeholder="Label" value={label}
            onChange={e => setLabel(e.target.value)} />
        </div>
      </div>
      <div className="mt-2">
        <Button variant="secondary" size="sm" onClick={handleAdd}
          disabled={add.isPending || !system.trim() || !reference.trim()}>
          Add reference
        </Button>
      </div>
      {add.error && <p className="text-sm text-destructive mt-2">{recordErrorMessage(add.error)}</p>}
      {remove.error && <p className="text-sm text-destructive mt-2">{recordErrorMessage(remove.error)}</p>}
    </RecordSection>
  )
}
